// @ts-check
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FilterPanel, FilterItem, FilterActionContainer } from '../FilterPanel';
import Select from 'react-select';
import actions from '../actions';

/** @typedef {{
  assignedAgentId: string | null;
  specialtyId: string | null;
  locationId: string | null;
  providerIds: string | null;
  referralTagId: string | null;
}} ReceivedFilter */

/** @typedef {{
 initialValue: ReceivedFilter,
 appliedValue: ReceivedFilter,
 onApply: (value: ReceivedFilter) => void
}} ReceivedFilterPanelProps */

/** @typedef {{
  filter: ReceivedFilter;
  loading: boolean;
  data: unknown;
  error?: unknown;
}} ReceivedFilterPanelState */

/** @type { React.ValidationMap<ReceivedFilterPanelProps> } */
const propTypes = {
  initialValue: PropTypes.object.isRequired,
  appliedValue: PropTypes.object.isRequired,
  onApply: PropTypes.func.isRequired,
};

function areEqualShallow(a, b) {
  for (let key in a) {
    if (!(key in b) || a[key] !== b[key]) {
      return false;
    }
  }
  for (let key in b) {
    if (!(key in a) || a[key] !== b[key]) {
      return false;
    }
  }
  return true;
}

const unassignedAgentOption = {
  id: 'UNASSIGNED',
  name: 'UNASSIGNED',
};

function addUnassignedOption(options) {
  return [unassignedAgentOption, ...options];
}

/** @augments { Component<ReceivedFilterPanelProps, ReceivedFilterPanelState> } */
class ReceivedFilterPanel extends Component {
  static propTypes = propTypes;

  constructor(props) {
    super(props);
    this.state = {
      filter: this.props.initialValue,
      loading: true,
      data: null,
      error: null,
    };
    this.fetchFilterValues = this.fetchFilterValues.bind(this);
    this.handleApply = this.handleApply.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleClear = this.handleClear.bind(this);
  }

  componentDidMount() {
    this.fetchFilterValues();
  }

  fetchFilterValues() {
    this.setState({ loading: true, error: null });
    actions.fetchReceivedFilterOptions().then((data) => {
      this.setState({
        loading: false,
        error: null,
        data: {
          availableAgents: data.availableAgents,
          referrableSpecialties: data.referrableSpecialties,
          locations: data.locations,
          providers: data.providers,
          referralTags: data.referralTags,
        },
      });
    });
  }

  /**
   *
   * @param {keyof ReceivedFilter} key
   * @param {string | null} value
   */
  handleChange(key, value) {
    this.setState(({ filter }) => ({ filter: { ...filter, [key]: value } }));
  }

  handleApply() {
    const { filter } = this.state;
    const { onApply } = this.props;
    onApply(filter);
  }

  handleClear() {
    const { initialValue } = this.props;
    this.setState({ filter: initialValue }, () => {
      this.handleApply();
    });
  }

  render() {
    const { filter, loading, data, error } = this.state;
    const { appliedValue } = this.props;

    const canClear = Object.keys(appliedValue).some(
      (key) => appliedValue[key] !== null
    );
    const isApplied = areEqualShallow(appliedValue, filter);

    const agentOptions = addUnassignedOption(
      (data || {}).availableAgents || []
    );
    const specialtyOptions = (data || {}).referrableSpecialties || [];
    const locationOptions = addUnassignedOption((data || {}).locations || []);
    const providerOptions = addUnassignedOption((data || {}).providers || []);
    const referralTagOptions = (data && data.referralTags) || [];

    return (
      <FilterPanel>
        {error ? (
          <p>Failed to load</p>
        ) : (
          <>
            <FilterItem label="Assigned To">
              <Select
                value={filter.assignedAgentId}
                isLoading={loading || !data}
                options={agentOptions}
                placeholder="Any"
                labelKey="name"
                valueKey="id"
                onChange={(val) => this.handleChange('assignedAgentId', val)}
                clearable
                simpleValue
              />
            </FilterItem>
            <FilterItem label="Specialty">
              <Select
                value={filter.specialtyId}
                isLoading={loading || !data}
                options={specialtyOptions}
                placeholder="Any"
                labelKey="name"
                valueKey="id"
                onChange={(val) => this.handleChange('specialtyId', val)}
                clearable
                simpleValue
              />
            </FilterItem>
            <FilterItem label="Location">
              <Select
                value={filter.locationId}
                isLoading={loading || !data}
                options={locationOptions}
                placeholder="Any"
                labelKey="name"
                valueKey="id"
                onChange={(val) => this.handleChange('locationId', val)}
                clearable
                simpleValue
              />
            </FilterItem>
            <FilterItem label="Provider">
              <Select
                value={filter.providerIds}
                isLoading={loading || !data}
                options={providerOptions}
                placeholder="Provider"
                labelKey="name"
                valueKey="id"
                onChange={(val) => this.handleChange('providerIds', val || null)}
                clearable
                simpleValue
                multi
              />
            </FilterItem>
            <FilterItem label="Tag">
              <Select
                value={filter.referralTagId}
                isLoading={loading || !data}
                options={referralTagOptions}
                placeholder="Tag"
                labelKey="label"
                valueKey="id"
                onChange={(val) => this.handleChange('referralTagId', val)}
                clearable
                simpleValue
              />
            </FilterItem>
            {/* <FilterItem label="Order By">
          <div className="select">
            <select>
              <option>Hello</option>
              <option>World</option>
            </select>
          </div>
        </FilterItem> */}
            <FilterActionContainer>
              <button
                type="button"
                disabled={isApplied}
                className="button is-link"
                onClick={this.handleApply}
              >
                Apply
              </button>
              {canClear ? (
                <button
                  type="button"
                  onClick={this.handleClear}
                  style={{
                    display: 'block',
                    border: 'none',
                    background: 'none',
                    color: '#e53e3e',
                    borderBottom: '1px #e53e3e dashed',
                    fontSize: '0.75rem',
                    padding: 0,
                    lineHeight: 1.1,
                    marginTop: '0.5rem',
                  }}
                >
                  Reset Filters
                </button>
              ) : null}
            </FilterActionContainer>
          </>
        )}
      </FilterPanel>
    );
  }
}

export { ReceivedFilterPanel };
